import { useMemo } from "react";
import Chart from "react-apexcharts";
import styled from "styled-components";

// #region SCOPED CSS
const StyledChart = styled.div`
    width: 100%;
    min-width: 0;
`;
// #endregion

const formatTooltipValue = value => {
    const formattedValue = new Intl.NumberFormat("de-DE").format(Math.abs(value));
    if (value === 0) return formattedValue;
    return `${value > 0 ? "+" : "-"}${formattedValue}`;
};

/**
 * Standard horizontal bar chart for positive and negative values around zero.
 *
 * @param {Object} props
 * @param {{ label: string, value: number, color?: string }[]} props.items
 * @param {string} [props.seriesName]
 * @param {number} [props.height]
 * @param {number} [props.min]
 * @param {number} [props.max]
 * @param {(value: number) => string} [props.valueFormatter]
 * @param {string} [props.ariaLabel]
 */
export default function ChartDivergingBars({
    items = [],
    seriesName = "Variance",
    height = 220,
    min,
    max,
    valueFormatter = formatTooltipValue,
    ariaLabel = "Positive and negative horizontal bar chart"
}) {
    const series = useMemo(
        () => [
            {
                name: seriesName,
                data: items.map(item => ({
                    x: item.label,
                    y: item.value,
                    fillColor: item.color
                }))
            }
        ],
        [items, seriesName]
    );

    const options = useMemo(
        () => ({
            chart: {
                type: "bar",
                background: "transparent",
                toolbar: { show: false },
                animations: { enabled: true },
                redrawOnWindowResize: true,
                redrawOnParentResize: true
            },
            plotOptions: {
                bar: {
                    horizontal: true,
                    distributed: true,
                    barHeight: "56%",
                    borderRadius: 0
                }
            },
            dataLabels: { enabled: false },
            legend: { show: false },
            stroke: { show: false },
            fill: { opacity: 1 },
            grid: {
                show: true,
                borderColor: "#3c485a",
                xaxis: { lines: { show: true } },
                yaxis: { lines: { show: false } },
                padding: { top: -8, right: 8, bottom: -8, left: 0 }
            },
            xaxis: {
                min,
                max,
                labels: { show: false },
                axisBorder: { show: false },
                axisTicks: { show: false },
                tooltip: { enabled: false }
            },
            yaxis: {
                labels: {
                    maxWidth: 90,
                    style: {
                        colors: ["#d1d1d1"],
                        fontSize: "10px"
                    }
                }
            },
            annotations: {
                xaxis: [
                    {
                        x: 0,
                        borderColor: "#7c868b",
                        strokeDashArray: 0
                    }
                ]
            },
            tooltip: {
                enabled: true,
                theme: "dark",
                fillSeriesColor: false,
                marker: { show: false },
                x: { show: true },
                y: {
                    title: { formatter: () => "" },
                    formatter: valueFormatter
                }
            },
            noData: {
                text: "No chart data",
                style: { color: "#7c868b" }
            }
        }),
        [max, min, valueFormatter]
    );

    return (
        <StyledChart role="img" aria-label={ariaLabel}>
            <Chart type="bar" height={height} width="100%" options={options} series={series} />
        </StyledChart>
    );
}

